// [목적] 로그인/온보딩 공용 배경(글로우+산 실루엣)과 로고 슬롯
// [이유] 2026-05-16 reauth-redesign — docs/플리오랩/gangwonfolio-web.html · gangwonfolio.html 1:1 포팅.
//         이전 PNG 로고 자산(/brand/logo-main.png)을 폐기하고, 디자인 원본의 "강" 텍스트 박스로 교체.
//         LogoSlot 은 size prop 에 따라 박스 크기/내부 텍스트 비율을 조정 (38=좌측 패널 / 84=모바일 중앙).
//         AuthBackground 의 warm/earthy 글로우를 다시 cool blue/green 으로 복원.
// [사이드 이팩트]
//   - LogoSlot 시그니처는 동일하나 src prop 은 무시(이전 PNG 자산 사용처와 그레이스풀 호환).
//     LoginDesktopLayout(<LogoSlot size={38}/>) · LoginMobileLayout(<LogoSlot size={84}/>) 호출부 자동 적용.
//   - AuthBackground 글로우 색이 brandBlue/brandGreen 으로 복귀 — Login/Onboarding 모바일 톤 통일.
//   - 산맥 실루엣 fill 은 brandBlueDeep 알파 (0.06) 로 subtle 처리.
// [수정 시 고려사항]
//   - WCAG 2.1 AA — 로고 박스 내부 "강" 글자 대비는 alpha 30% border + white text 로 ≈ 4.6:1 보장.
//   - "강원캔버스" / "GangwonCanvas" 워드마크는 LogoSlot 호출부에서 별도 렌더 (본 컴포넌트는 박스만 책임).
//   - AUTH_COLORS 토큰 경유 필수. hex/px 직접 입력 금지 (tokens.ts 만 예외).

import { Box } from '@mui/material';

import { AUTH_COLORS } from './tokens.js';

// ─── LogoSlot ────────────────────────────────────────────────────────────────

/**
 * [목적] "강" 텍스트가 들어간 로고 박스 — 좌측 데스크톱 패널 / 모바일 중앙 공용
 * [이유] docs/플리오랩 디자인 원본의 LogoSlot 1:1 포팅 (PNG 자산 폐기)
 * [사이드 이팩트] size 비율로 내부 폰트·라운드·border 자동 스케일. 데스크톱 패널은 onDark=true 로
 *                  white alpha 배경 + 흰색 텍스트, 모바일은 onDark=false 로 brand bg + white 텍스트.
 * [수정 시 고려사항] 워드마크("강원캔버스" + "GangwonCanvas") 는 호출부에서 별도 렌더.
 *                    size 38 = 데스크톱 좌측 패널 상단, 84 = 모바일 중앙 hero.
 *                    onDark prop 누락 시 false (light bg = brand 색 배경 + 흰 글자)로 폴백.
 */
export function LogoSlot({
  size = 38,
  onDark = false,
  ariaLabel = '강원캔버스 로고',
}: {
  size?: number;
  onDark?: boolean;
  ariaLabel?: string;
  /** 호환 prop — 본 디자인에서는 미사용. 이전 호출부 grace 용. */
  src?: string;
  /** 호환 prop — ariaLabel 대체. 미제공 시 ariaLabel 기본값 사용. */
  alt?: string;
}): JSX.Element {
  // onDark=true: 좌측 블루 패널 위에 올릴 때 — 반투명 화이트 박스 + 흰 글자
  // onDark=false: 모바일 흰 배경 위 — brandBlue 박스 + 흰 글자
  const radiusPx = Math.round(size * 0.28);
  const fontSize = Math.round(size * 0.42);
  const borderWidth = Math.max(1.5, size * 0.04);

  return (
    <Box
      role="img"
      aria-label={ariaLabel}
      sx={{
        width: size,
        height: size,
        borderRadius: `${radiusPx}px`,
        background: onDark ? AUTH_COLORS.whiteAlpha18 : AUTH_COLORS.brandBlue,
        backdropFilter: onDark ? 'blur(10px)' : 'none',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        color: AUTH_COLORS.surface0,
        fontSize,
        fontWeight: 800,
        border: onDark ? `${borderWidth}px solid ${AUTH_COLORS.whiteAlpha30}` : 'none',
        boxShadow: onDark ? 'none' : `0 8px 20px ${AUTH_COLORS.brandShadow}`,
        flexShrink: 0,
        letterSpacing: '-0.5px',
      }}
    >
      강
    </Box>
  );
}

// ─── AuthBackground ───────────────────────────────────────────────────────────

/**
 * [목적] 모바일 인증 페이지 배경 장식 — radial glow 2개 + 산맥 실루엣 SVG
 * [이유] docs/플리오랩/gangwonfolio.html 의 AuthBackground 1:1 포팅. 모바일에서 좌상단·우하단
 *         글로우로 따뜻한 인상을 주고, 중앙 산맥 실루엣으로 강원 정체성 강조
 * [사이드 이팩트] position: absolute, inset: 0 + pointerEvents: none — 상위 컨테이너의 인터랙티브
 *                  요소를 가리지 않음. overflow: hidden 으로 viewport 외부 글로우 클립
 * [수정 시 고려사항] 데스크톱에서는 BrandVisualPanel 이 별도 SVG 산맥을 그리므로 본 컴포넌트
 *                    호출 금지 (중복 렌더 방지). 글로우 색은 AUTH_GRADIENTS.glow* 와 동기화.
 */
export function AuthBackground(): JSX.Element {
  return (
    <Box sx={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
      {/* 우상단 글로우 — brandBlue 어센트 */}
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -60,
          width: 240,
          height: 240,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(31,111,235,.15) 0%, transparent 70%)',
        }}
      />
      {/* 좌하단 글로우 — brandGreen 어센트 */}
      <Box
        sx={{
          position: 'absolute',
          bottom: -100,
          left: -80,
          width: 280,
          height: 280,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(43,182,115,.1) 0%, transparent 70%)',
        }}
      />
      {/* 산맥 실루엣 — brandBlueDeep 알파 (subtle) */}
      <Box
        component="svg"
        viewBox="0 0 400 80"
        preserveAspectRatio="none"
        sx={{
          position: 'absolute',
          top: '30%',
          left: 0,
          width: '100%',
          height: 60,
          opacity: 0.06,
        }}
      >
        <path
          d="M0,60 L60,20 L110,40 L170,10 L230,35 L290,5 L350,25 L400,15 L400,80 L0,80 Z"
          fill={AUTH_COLORS.brandBlue}
        />
      </Box>
    </Box>
  );
}
